/**
 * Рекурсивный рендерер `NarrativeBlock`.
 *
 * Два режима блока:
 *  - **entity** (`block.entity` задан): кликабельный заголовок «Тип «Имя»»,
 *    под ним поля + цитаты + health. Hover-фон даёт визуальный сигнал
 *    что блок ведёт в EntityCard drawer.
 *  - **label-aggregate** (без entity): мелкий заголовок секции + children
 *    («Альтернативы», «ERRC», «Услуги»…).
 *
 * `showFields` — поля entity-блоков. По умолчанию **выключены**: в
 * канвасных нарративах key/value внутри карточки создавали шум, а детали
 * и так открываются в EntityCard. Но там, где поля и есть содержание
 * («Что решили и почему»: вопрос → выбрали → отвергли → почему), без них
 * остаётся голый список имён — такие аспекты включают флаг явно
 * (см. FIELD_ASPECTS в NarrativeAspectView).
 *
 * Mobile-tuning:
 *  - indent multiplier: 12px desktop → 8px mobile (на 5 уровней глубины
 *    разница 60 vs 40 px).
 *  - border-padding-left: 10 → 8 на mobile.
 *  - fields key min-width: 88 → 72 px (имена полей всё ещё помещаются).
 */

import { Box, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { NarrativeBlock } from '@pflow/shared/v2';
import { CitationList } from './CitationChip';
import { HealthInlineList } from './HealthInlineFlag';
import { useUiStore } from '../stores/ui-store';
import { DESKTOP_QUERY } from '../lib/breakpoints';

interface Props {
    block: NarrativeBlock;
    depth?: number;
    /** Показывать key/value поля entity-блоков (см. шапку файла). */
    showFields?: boolean;
}

export function NarrativeBlockView({ block, depth = 0, showFields = false }: Props): JSX.Element {
    const openCard  = useUiStore(s => s.openCard);
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true) ?? true;
    const indentStep    = isDesktop ? 12 : 8;
    const borderPadLeft = isDesktop ? 10 : 8;
    const indent = depth * indentStep;
    const hasFields    = block.fields && block.fields.length > 0;
    const hasCitations = block.citations && block.citations.length > 0;
    const hasHealth    = block.health && block.health.length > 0;
    const hasChildren  = block.children && block.children.length > 0;

    // ── label-aggregate (нет entity) ─────────────────────────────────
    if (!block.entity) {
        return (
            <Box pl={indent} mt={depth > 0 ? 6 : 0}>
                {block.label && (
                    <Text fw={500} fz="xs" c="dimmed" mb={2}>{block.label}</Text>
                )}
                {hasFields && <FieldsList fields={block.fields!} />}
                {hasChildren && (
                    <Stack gap={6} mt={6}>
                        {block.children!.map((c, i) => (
                            <NarrativeBlockView key={i} block={c} depth={depth + 1} showFields={showFields} />
                        ))}
                    </Stack>
                )}
            </Box>
        );
    }

    // ── entity-блок: кликабельный header + поля ───────────────────────
    const entity = block.entity;
    return (
        <Box
            pl={indent}
            py={6}
            style={depth === 0
                ? undefined
                : { borderLeft: '2px solid var(--mantine-color-default-border)', paddingLeft: borderPadLeft }}
            mt={depth > 0 ? 4 : 0}
        >
            <UnstyledButton
                onClick={() => openCard(entity)}
                className="pflow-entity-trigger"
                style={{
                    display: 'block',
                    width: '100%',
                    borderRadius: 4,
                    margin: '-4px -6px 2px',
                    padding: '4px 6px',
                }}
            >
                {/* Тип отдельной мелкой строкой сверху — не давит на имя,
                   читается как label. Имя — основная цель клика, обычным
                   подчёркиванием при hover (см. CSS-правило ниже). */}
                <Text fz="xs" c="dimmed" mb={2} lh={1.2}>{entity.type}</Text>
                <Text fw={500} fz="sm" lh={1.3} style={{ wordBreak: 'break-word' }}>
                    «{entity.name}»
                    {entity.canvas && (
                        <Text span fz="xs" c="dimmed" ml={6} fs="italic">
                            в «{entity.canvas}»
                        </Text>
                    )}
                </Text>
            </UnstyledButton>
            {showFields && hasFields && <FieldsList fields={block.fields!} />}
            {hasCitations && (
                <Box mt={6}>
                    <CitationList citations={block.citations!} />
                </Box>
            )}
            {hasHealth    && <HealthInlineList flags={block.health!} />}
            {hasChildren && (
                <Stack gap={6} mt={10}>
                    {block.children!.map((c, i) => (
                        <NarrativeBlockView key={i} block={c} depth={depth + 1} showFields={showFields} />
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function FieldsList({ fields }: {
    fields: NonNullable<NarrativeBlock['fields']>;
}): JSX.Element {
    // Каждое поле — два ряда: ключ (xs/dimmed) сверху, значение (sm) снизу.
    // Раньше был tabular layout с фиксированной колонкой ключа (88/72px) —
    // при длинных ключах вроде «Платёжеспособность» или «Тип_договорённости»
    // верстка ехала: либо ключ truncate'ился, либо колонки не выравнивались
    // между соседними полями. Stack даёт однородный ритм независимо от длины.
    return (
        <Stack gap={6} mt={4}>
            {fields.map((f, i) => (
                <div key={i}>
                    <Text fz="xs" c="dimmed" lh={1.2} style={{ wordBreak: 'break-word' }}>
                        {f.key}
                    </Text>
                    <Text fz="sm" lh={1.4} style={{ wordBreak: 'break-word' }}>
                        {String(f.value)}
                    </Text>
                </div>
            ))}
        </Stack>
    );
}
